/**
 * Holds pending "bank this lesson" proposals emitted by the agent's
 * study.proposeLesson UI intent, keyed by the board channel the lesson
 * belongs to.
 *
 * Callable from non-React code via getState().propose(); the CaptureCard
 * rendered on the matching Study board surface reads its pending proposal via a
 * selector and offers Keep / Edit / Dismiss. One pending proposal per channel
 * (a newer proposal replaces the prior).
 */
import { create } from 'zustand';

import { registerUserScopedReset } from '@shared/services/userScopedState';

/** A single pending lesson proposal awaiting the user's decision. */
export interface LessonProposal {
  /** The proposed lesson text. */
  content: string;
}

interface LessonCaptureState {
  /** Pending proposals indexed by board channel id. */
  proposalsByChannel: Record<string, LessonProposal>;
  /** Record (or replace) the pending proposal for a channel. */
  propose: (channelId: string, content: string) => void;
  /** Remove the pending proposal for a channel (kept or dismissed). */
  clear: (channelId: string) => void;
}

export const useLessonCaptureStore = create<LessonCaptureState>((set) => ({
  proposalsByChannel: {},
  propose: (channelId, content) =>
    set((s) => ({
      proposalsByChannel: { ...s.proposalsByChannel, [channelId]: { content } },
    })),
  clear: (channelId) =>
    set((s) => {
      if (!(channelId in s.proposalsByChannel)) return s;
      const next = { ...s.proposalsByChannel };
      delete next[channelId];
      return { proposalsByChannel: next };
    }),
}));

registerUserScopedReset('lesson-proposals', () =>
  useLessonCaptureStore.setState({ proposalsByChannel: {} })
);
